/**
 * @fileoverview Template-to-run-flow-state projection helpers.
 * @module import-editor/unifiedWizard/templateSelection
 *
 * Responsibilities:
 * - Project a full `ImportTemplate` into the run-flow list-item shape when
 *   the template list row is unavailable (`toSelectedTemplate`).
 * - Compute the run-flow state patch applied when the user selects a
 *   template (`buildTemplateSelectionPatch`) — mapping seeding, shell
 *   match-key pre-seed, boundary-check defaults, and data-mode propagation.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for template types
 * - `../runFlow/runFlowTypes` for the state patch target
 *
 * Architecture:
 * - Pure projections — no I/O, no React, no host wiring. The run-flow hook
 *   applies the returned patch; hosts never re-implement these rules.
 *
 * Notes:
 * - Extracted from system-web's SystemImportWizardContainer
 *   (`applyTemplateToState` / `toSelectedTemplate`) during the Phase 2A
 *   run-flow migration; tenant-web's container carried the same rules
 *   inline. Both legacy containers are retired by G-002.
 */

import type {
  ImportTemplate,
  ImportTemplateListItem,
  ImportDataMode,
} from '@firesquirrel/local-db';
import type { ImportRunFlowState } from '../runFlow/runFlowTypes';

/**
 * @reusable toSelectedTemplate
 * Usage:        Run flow template hydration when the seeded template is not
 *               present in the loaded template list (e.g. deep-link resume).
 * Rules:        Pure projection. Prefers the listed row when available so
 *               list-computed fields (fieldMappingCount, lastRunAt) stay
 *               consistent with the picker.
 *
 * Project a full template into the `ImportTemplateListItem` shape the run
 * flow stores on `selectedTemplate`.
 */
export function toSelectedTemplate(
  template: ImportTemplate,
  listed?: ImportTemplateListItem,
): ImportTemplateListItem {
  if (listed) return listed;
  return {
    _id: template._id,
    name: template.name,
    description: template.description,
    scope: template.scope,
    dataMode: template.dataMode,
    importBehavior: template.importBehavior,
    parentTemplateId: template.parentTemplateId,
    visibility: template.visibility,
    fieldMappingCount: template.fieldMappings?.length ?? 0,
    isDraft: template.isDraft,
    status: template.status,
    origin: template.origin,
    originTenantId: template.originTenantId,
    lastRunAt: template.lastRunAt,
    lastRunStatus: template.lastRunStatus,
    totalRuns: template.totalRuns,
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
    source: template.source,
  };
}

/**
 * @reusable buildTemplateSelectionPatch
 * Usage:        Run flow hook applies this patch when a template is selected
 *               (both the pick-from-list and deep-link hydration paths).
 * Rules:        Pure. Shell templates (no persisted mappings, no parent)
 *               pre-seed only the declared match-key target so the user
 *               supplies mappings at import time.
 *
 * Compute the run-flow state patch for a selected template:
 * - seeds `fieldMappings` from the template (or the shell match-key seed)
 * - propagates `importBehavior` and `dataMode` (template first, then the
 *   list item, defaulting to 'parcel' for legacy docs with no dataMode)
 * - captures the template's boundary-check default into both the read-only
 *   `templateBoundaryCheckDefault` and the run-scoped `boundaryCheckEnabled`
 */
export function buildTemplateSelectionPatch(
  template: ImportTemplate,
  selectedTemplate: ImportTemplateListItem,
): Partial<ImportRunFlowState> {
  const templateDefault = !!template.boundaryCheckEnabled;
  const hasMappings = !!template.fieldMappings?.length;
  const isShell = !hasMappings && !template.parentTemplateId;
  const seedMappings = hasMappings
    ? template.fieldMappings!
    : (isShell && template.matchKeyField
        ? [{
            targetField: template.matchKeyField,
            sourceColumns: [],
            isMatchKey: true,
          }]
        : []);

  const dataMode = (template.dataMode ?? selectedTemplate.dataMode ?? 'parcel') as ImportDataMode;
  return {
    selectedTemplate,
    fieldMappings: seedMappings,
    // parcelOwnerUpdate (Phase 5a) is update-only by contract — force
    // 'upsert' regardless of the template's stored behavior so insert /
    // append / full-sync are unreachable for the run.
    importBehavior: dataMode === 'parcelOwnerUpdate'
      ? 'upsert'
      : template.importBehavior ?? selectedTemplate.importBehavior ?? 'upsert',
    dataMode,
    templateBoundaryCheckDefault: templateDefault,
    boundaryCheckEnabled: templateDefault,
  };
}
